import React, { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, X, ArrowSquareOut, WarningCircle } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { ReceiptViewer } from "@/components/ReceiptViewer";
import api, { pagePictureUrl, receiptUrl } from "@/lib/api";
import {
  attachmentButtonLabel, attachmentGapAriaLabel, describeUnshowableFile,
  displayKindFor, unavailableAttachmentsLabel, PAPERCLIP_HELP,
  PAPERCLIP_HELP_GAP_LINE,
} from "@/lib/attachments";
import { InfoTip } from "@/components/common";

/**
 * Look at an attachment WITHOUT losing the row it belongs to. (ZEN-351)
 *
 * THE SHAPE, AND WHY IT IS THIS ONE. The whole point of opening a receipt from
 * a list is to check it against the payee, the amount, the currency and the
 * status sitting next to it. A window floating over the middle of the list
 * covers exactly the numbers you opened it to compare, so it is the wrong
 * shape here however convenient it is elsewhere.
 *
 * Of the three shapes that keep the row visible — a panel down the side, a
 * window offset to one corner, and an expansion directly beneath the row —
 * only the expansion survives a phone. A side panel at 375 pixels wide has
 * nowhere to go but full screen, which is the covered-up row again under a
 * different name; an offset window has the same problem. An expansion is the
 * same layout at every width: the row keeps its place, the attachment appears
 * under it, and nothing is ever drawn on top of anything.
 *
 * NOTHING SCROLLS BY ITSELF when the panel opens. An automatic scroll is the
 * one thing that could push the row off the top of the screen, which is the
 * single behaviour this feature exists to prevent.
 */

// ─── Loading one file ────────────────────────────────────────────────────────
//
// The file is FETCHED rather than handed straight to an <img> or a frame, and
// that is deliberate. Handing the URL over means the browser decides silently
// what to do with whatever comes back: a deleted file shows as a broken image
// in one case and an empty grey box in another, and a spreadsheet downloads
// itself. Fetching first means the panel knows three things before it draws
// anything — whether the file is still there, what the server says it is, and
// what to say if it is not — which is what ZEN-82 settled: a missing file
// announces itself instead of impersonating a real one.
//
// A PICTURE THAT CANNOT BE MADE IS NOT THE END. When `fallbackUrl` is given
// (the original file, behind a page picture) and the picture fails — an error
// answer, no answer, or contents that cannot be read — the original is asked
// for instead and whatever it answers is shown,
// exactly as before page pictures existed. A file that is gone still says so
// and an expired pass is still reported as one, because the original answers
// the same way. It also keeps these screens working on a server that has no
// picture addresses yet: merged screens go live before the server. A request
// that never finishes keeps the loading circle up, as the original's would;
// no time limit, by owner ruling. (ZEN-419)
function useFile(url, fallbackUrl) {
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    if (!url) { setState({ status: "nofile" }); return undefined; }
    let cancelled = false;
    let objectUrl = null;
    setState({ status: "loading" });

    // Turns a successful answer into what the panel draws.
    const read = async (res, fromPicture) => {
      const contentType = res.headers.get("Content-Type") || "";
      const kind = displayKindFor(contentType);
      if (kind === "text") return { status: "ready", kind, text: await res.text() };
      const pages = parseInt(res.headers.get("X-Page-Count"), 10);
      const blob = await res.blob();
      if (cancelled) return null;   // closed while loading: make nothing to release
      objectUrl = URL.createObjectURL(blob);
      return { status: "ready", kind, contentType, objectUrl, fromPicture,
        pageCount: fromPicture && pages > 0 ? pages : null };
    };

    (async () => {
      // The picture is used only if it arrives whole. No answer, an error
      // answer, or a body that cannot be read all mean: ask for the original.
      if (fallbackUrl) {
        try {
          const res = await fetch(url);
          if (res.ok) {
            const ready = await read(res, true);
            if (!cancelled && ready) setState(ready);
            return;
          }
        } catch { /* fall through to the original */ }
      }
      try {
        const res = await fetch(fallbackUrl || url);
        if (!res.ok) {
          // The server's own words where it has any — "Document not found"
          // is more useful than a status code, and it is the truth.
          let detail = "";
          try {
            const body = await res.clone().json();
            if (typeof body?.detail === "string") detail = body.detail;
          } catch { /* not JSON — the status alone will have to do */ }
          if (cancelled) return;
          // A REFUSAL IS NOT A DELETION, and saying so would be a lie about
          // somebody's evidence. The file URLs carry a short-lived credential
          // that expires in minutes; a page left open past a rotation gets a
          // 401, and "this file is no longer in storage" would send the owner
          // looking for a document that is sitting there perfectly safe.
          setState({ status: res.status === 404 ? "missing" : "refused", httpStatus: res.status, detail });
          return;
        }
        const ready = await read(res, false);
        if (!cancelled && ready) setState(ready);
      } catch {
        if (cancelled) return;
        setState({ status: "unreachable" });
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, fallbackUrl]);

  return state;
}

function Notice({ tone = "plain", icon = true, children, testid }) {
  const tones = {
    plain: "border-[#C2C9D1] bg-[#F8FAFC] text-[#374151]",
    warn: "border-[#F01919]/30 bg-[#F01919]/5 text-[#991B1B]",
  };
  return (
    <div className={`flex items-start gap-2 rounded-lg border p-4 text-sm ${tones[tone]}`} data-testid={testid}>
      {icon && <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0" />}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function OpenInNewTab({ url, label, testid }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testid}
      className="mt-2 inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-md bg-[#034AA2]/10 px-3 py-2 text-xs font-semibold text-[#034AA2] hover:bg-[#034AA2]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#034AA2] focus-visible:ring-offset-1">
      <ArrowSquareOut size={15} weight="bold" /> {label}
    </a>
  );
}

// ─── One attachment, shown ───────────────────────────────────────────────────
function AttachmentBody({ attachment, testbase }) {
  // ASK THE CHEAPEST URL THAT ANSWERS THE QUESTION. Where the server can hand
  // back a PICTURE of page one — receipts, and since ZEN-419 expense,
  // withdrawal and school paperwork documents — that is the version this panel
  // goes on to display, so fetching the original as well would pull a whole
  // multi-megabyte PDF down a phone connection and throw it away. For anything
  // else the file itself is the only thing to ask for.
  //
  // The picture is asked for at exactly the address the viewer will use next,
  // so the second request is served from the browser's cache rather than the
  // network.
  // A later page that cannot be drawn switches the panel to the original too,
  // so every picture failure falls back the same way the first page does.
  // Kept per attachment, so a failure never carries over to the next one.
  const [failedKey, setFailedKey] = useState(null);
  const pictureFailed = failedKey === attachment.key;
  const pictureUrl = pictureFailed ? null : pagePictureUrl(attachment, 0);
  const state = useFile(pictureUrl || attachment.fileUrl, pictureUrl ? attachment.fileUrl : null);

  if (state.status === "nofile") {
    return (
      <Notice testid={`${testbase}-nofile`}>
        {attachment.noFileNote || "This attachment has no file stored against it, so there is nothing to show."}
      </Notice>
    );
  }
  if (state.status === "loading") {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border border-[#C2C9D1] bg-[#F8FAFC]" data-testid={`${testbase}-loading`}>
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#C2C9D1] border-t-[#034AA2]" />
        <span className="sr-only">Opening the attachment</span>
      </div>
    );
  }
  if (state.status === "missing") {
    return (
      <Notice tone="warn" testid={`${testbase}-missing`}>
        <p className="font-bold">This file is no longer in storage.</p>
        <p className="mt-1">
          The record still says a file is attached, but the file itself cannot be found, so there is nothing to show.
          {state.detail ? ` The server said: ${state.detail}` : ""}
        </p>
      </Notice>
    );
  }
  if (state.status === "refused") {
    const expired = state.httpStatus === 401 || state.httpStatus === 403;
    return (
      <Notice tone="warn" testid={`${testbase}-refused`}>
        <p className="font-bold">
          {expired ? "This attachment could not be opened." : "The server could not return this file."}
        </p>
        <p className="mt-1">
          {expired
            ? "The file is still there — the short-lived pass this page uses to fetch it has expired, which happens on a page left open for a while. Reload the page and try again."
            : `Something went wrong on the server (status ${state.httpStatus}), so the file could not be fetched. The file itself has not been touched.`}
          {state.detail ? ` The server said: ${state.detail}` : ""}
        </p>
      </Notice>
    );
  }
  if (state.status === "unreachable") {
    return (
      <Notice tone="warn" testid={`${testbase}-unreachable`}>
        This attachment could not be fetched — check your connection and try again.
      </Notice>
    );
  }

  // A file the server renders page by page is shown that way: it turns a
  // multi-page PDF into pictures, which is the only version that works on a
  // phone. Everything else is shown from the file itself.
  if (state.kind === "image" && state.fromPicture) {
    return (
      <>
        <ReceiptViewer
          key={attachment.key}
          getPageSrc={(page) => pagePictureUrl(attachment, page)}
          pageCount={state.pageCount || attachment.pageCount || 1}
          onPageError={() => setFailedKey(attachment.key)}
          alt={attachment.label || "Attachment"}
          areaClassName="h-[50vh] sm:h-[55vh] lg:h-[60vh]"
          errorMessage="This attachment could not be displayed. Open it in a new tab to check it."
        />
        {/* The ORIGINAL, not the picture of it — a PDF is the document
            an accountant would want, and the pictures above are only how it is
            read here. */}
        <OpenInNewTab url={attachment.fileUrl} label="Open the original file in a new tab" testid={`${testbase}-open`} />
      </>
    );
  }

  if (state.kind === "pdf") {
    return (
      <div data-testid={`${testbase}-pdf`}>
        {/* THE LINK AND THE WARNING COME FIRST, ABOVE THE FRAME, ON PURPOSE.
            A computer shows a PDF inside the frame below with its own pages,
            zoom and search. Several phone browsers simply refuse to, and leave
            a blank rectangle — and a blank rectangle with no explanation is
            the app pretending to show a document it is not showing, which is
            the thing ZEN-82 settled we do not do. So the way out is named
            BEFORE the frame, and the sentence says what an empty frame means
            rather than leaving somebody to guess. */}
        <p className="text-xs text-[#6b7280]" data-testid={`${testbase}-pdf-note`}>
          Shown below in your browser&rsquo;s own PDF reader. Some phone browsers will not display a
          PDF inside a page — if the box below is empty, that is why, and the button opens it properly.
        </p>
        <OpenInNewTab url={attachment.fileUrl} label="Open this PDF full screen" testid={`${testbase}-open`} />
        <iframe
          src={state.objectUrl}
          title={attachment.label || "Attachment"}
          data-testid={`${testbase}-pdf-frame`}
          className="mt-2 h-[50vh] w-full rounded-lg border border-[#C2C9D1] bg-[#F8FAFC] sm:h-[55vh] lg:h-[60vh]"
        />
      </div>
    );
  }

  if (state.kind === "image") {
    return (
      <ReceiptViewer
        key={attachment.key}
        getPageSrc={() => state.objectUrl}
        pageCount={1}
        alt={attachment.label || "Attachment"}
        areaClassName="h-[50vh] sm:h-[55vh] lg:h-[60vh]"
        errorMessage="This attachment could not be displayed — it may be in a picture format this browser cannot show. Open it in a new tab to check it."
      />
    );
  }

  if (state.kind === "text") {
    return (
      <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-[#C2C9D1] bg-[#F8FAFC] p-3 sm:max-h-[55vh]" data-testid={`${testbase}-text`}>
        <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-snug text-[#374151]">{state.text || "(this attachment is empty)"}</pre>
      </div>
    );
  }

  return (
    <Notice testid={`${testbase}-unshowable`}>
      <p className="font-bold">This is {describeUnshowableFile(state.contentType)}.</p>
      <p className="mt-1">A browser cannot show it here, so open it to read it in the program that handles it.</p>
      <OpenInNewTab url={attachment.fileUrl} label="Open this file" testid={`${testbase}-open`} />
    </Notice>
  );
}

/**
 * THE PAPERCLIP IS THE BUTTON. (ZEN-351, owner ruling.)
 *
 * It used to be decoration that told you a file existed and did nothing when
 * you clicked it, which is exactly the instinct anyone has. Now the instinct
 * is right — and because it is the ONLY way in, it has to be a real control:
 * reachable by keyboard, with a focus ring you can see, and a name that says
 * both what it opens and which record it belongs to.
 *
 * AND THE RULE THAT FALLS OUT OF IT: no paperclip, no view option. A row with
 * nothing attached renders nothing at all here — no greyed-out icon, no
 * placeholder, no tooltip apologising for itself. One symbol, one meaning.
 */
export const AttachmentButton = React.forwardRef(function AttachmentButton({
  attachments, recordLabel, expanded, onToggle, panelId, testid, variant = "icon",
}, ref) {
  const count = attachments?.length || 0;
  if (!count) return null;                       // nothing attached, nothing here

  const label = attachmentButtonLabel(count, recordLabel, expanded);
  const focus = "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#034AA2] focus-visible:ring-offset-1";

  // A paperclip drawn as decoration is smaller than a control has any business
  // being on a phone. So every layout starts at a full 44-pixel target and
  // only the widest one shrinks to 32.
  //
  // CHECKED RATHER THAN ASSUMED, and the check changed the answer: the
  // expenses TABLE is not a desktop-only layout. It appears from 640 pixels
  // up, which means it is what a tablet shows and what a finger touches. A
  // 32-pixel target there would have been decided by the word "table" instead
  // of by who is using it.
  if (variant === "chip") {
    return (
      <button ref={ref} type="button" onClick={onToggle} aria-expanded={!!expanded} aria-controls={panelId}
        aria-label={label} title={label} data-testid={testid}
        className={`inline-flex min-h-[2.75rem] max-w-full items-center gap-1.5 rounded-md bg-[#22C55E]/10 px-2.5 py-1 text-xs font-semibold text-[#077A39] transition-colors hover:bg-[#22C55E]/20 ${expanded ? "ring-2 ring-[#077A39]/40" : ""} ${focus}`}>
        <Paperclip size={14} weight="bold" className="shrink-0" />
        <span className="min-w-0 truncate">{attachments[0].label || "Document"}</span>
        {count > 1 && <span className="tnum shrink-0 text-[10px] font-bold">+{count - 1}</span>}
      </button>
    );
  }

  const size = variant === "dense" ? "h-11 w-11 lg:h-8 lg:w-8" : "h-11 w-11";
  return (
    <button ref={ref} type="button" onClick={onToggle} aria-expanded={!!expanded} aria-controls={panelId}
      aria-label={label} title={label} data-testid={testid}
      className={`inline-flex shrink-0 items-center justify-center gap-0.5 rounded-md text-[#077A39] transition-colors hover:bg-[#22C55E]/15 ${expanded ? "bg-[#22C55E]/15 ring-2 ring-[#077A39]/40" : ""} ${size} ${focus}`}>
      <Paperclip size={variant === "dense" ? 15 : 17} weight="bold" />
      {count > 1 && <span className="tnum text-[10px] font-bold">{count}</span>}
    </button>
  );
});

/**
 * The one thing a row says about paperwork that the paperclip cannot: this
 * record lists a receipt that is not in your records. (ZEN-455)
 *
 * NOTHING RENDERS FOR AN EMPTY ROW, by owner ruling 2026-09-02. The first
 * build put a quiet "No documents" on every empty row and she refused it as
 * clutter — correctly: that fact is already announced on the Overview and in
 * the check before the audit packet prints, and the paperclip convention
 * itself is explained by AttachmentHelp beside the list. So the row stays
 * clean and ZEN-351's "no placeholder" ruling stands untouched.
 *
 * IT REFUSES TO GUESS. The count must be a real, finite, non-negative number
 * or this renders nothing — an old cached row without the field must fall
 * back to silence rather than assert anything about somebody's evidence.
 */
export function AttachmentGap({ unavailableCount, recordLabel, testid }) {
  if (typeof unavailableCount !== "number" || !Number.isFinite(unavailableCount)) return null;
  if (unavailableCount < 1) return null;

  return (
    <span
      className="inline-flex min-w-0 items-center gap-1 rounded-md border border-[#D97706]/25 bg-[#D97706]/10 px-1.5 py-0.5 text-[11px] font-semibold leading-tight text-[#92400E]"
      aria-label={attachmentGapAriaLabel(unavailableCount, recordLabel)}
      data-testid={testid ? `${testid}-unavailable` : undefined}
    >
      <WarningCircle size={13} weight="fill" className="shrink-0" aria-hidden="true" />
      {/* WRAPS, never truncates. In the narrow payee cell an ellipsis ate the
          verb and left "1 document can't be…" — a caution that loses its verb
          to truncation reads as decoration. Two lines cost nothing; a half
          sentence costs the meaning. */}
      <span className="min-w-0">{unavailableAttachmentsLabel(unavailableCount)}</span>
    </span>
  );
}

/**
 * The paperclip convention, explained where the list is. (ZEN-455, owner
 * request 2026-09-01: "what we really need is in the header an InfoTip".)
 *
 * THIS IS THE FIX FOR THE ORIGINAL COMPLAINT, and it costs no row space. A
 * row with no paperclip used to be ambiguous — nothing attached, or no way to
 * look? Explaining the convention once removes the ambiguity everywhere at
 * once, which a per-row label could only do one row at a time.
 *
 * WHERE IT GOES IS A LAYOUT QUESTION, not a taste one: the expenses TABLE
 * only exists from 640px up, so a column-header tip is invisible on a phone —
 * exactly the device the owner raised. Each screen therefore places it where
 * that screen's reader will find it (the column header on a computer, the
 * page name on a phone and on the card-based screens), and this component
 * carries the words so all of them say the same thing.
 */
export function AttachmentHelp({ withGapLine = false, testid, side = "bottom" }) {
  return (
    <InfoTip testid={testid} side={side} width="w-80">
      {PAPERCLIP_HELP.map((line) => (
        <p key={line} className="mb-2 last:mb-0">{line}</p>
      ))}
      {withGapLine && <p className="mb-0 mt-2 font-semibold text-[#92400E]">{PAPERCLIP_HELP_GAP_LINE}</p>}
    </InfoTip>
  );
}

/**
 * The expansion itself. Rendered by the row, directly beneath it, never over
 * it. Escape closes it and hands focus back to the paperclip that opened it,
 * which is what a keyboard user expects and the only way back without a mouse.
 */
export function AttachmentPanel({ id, attachments, recordLabel, onClose, testid }) {
  const [selected, setSelected] = useState(0);
  const ref = useRef(null);
  const list = attachments || [];
  const current = list[Math.min(selected, list.length - 1)];

  const close = useCallback(() => { onClose?.(); }, [onClose]);

  // Focus moves into the panel when it opens so a keyboard reader lands on
  // what they just asked for. It is NOT a trap: the panel is beside the row,
  // not over it, so Tab must be able to walk straight back out to the list.
  useEffect(() => { ref.current?.focus?.(); }, []);

  if (!list.length) return null;

  return (
    <div
      ref={ref}
      id={id}
      tabIndex={-1}
      role="group"
      aria-label={recordLabel ? `Attachments for ${recordLabel}` : "Attachments"}
      onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); close(); } }}
      data-testid={testid}
      className="mt-2 rounded-lg border border-[#034AA2]/25 bg-[#F7F8FA] p-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#034AA2]"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">
            {list.length === 1 ? "Attachment" : `${list.length} attachments`}
          </div>
          {/* WRAPS, never truncates. On a phone the record's own figures can
              easily be scrolled above the fold by the time the panel is open,
              and this line is then the only place the amount and the date
              appear beside the document. Cutting it off with an ellipsis takes
              away the very thing being checked. */}
          {recordLabel && <div className="text-xs font-semibold leading-snug text-[#1f2937]" data-testid={`${testid}-record`}>{recordLabel}</div>}
        </div>
        <button type="button" onClick={close} data-testid={`${testid}-close`}
          aria-label={recordLabel ? `Close the attachment view for ${recordLabel}` : "Close the attachment view"}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#1f2937] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#034AA2]">
          <X size={17} weight="bold" />
        </button>
      </div>

      {/* SEVERAL ATTACHMENTS: NAMED LIST FIRST, first one already open.
          An expense can hold many since ZEN-79, and they are usually different
          documents — an invoice and a proof of payment, not two halves of one
          thing. Paging blindly from one to the next hides which is which, so
          the names are always on screen and picking one swaps the view. The
          first opens straight away because looking at it is why you clicked. */}
      {list.length > 1 && (
        <div className="mb-2 flex flex-wrap gap-1.5" role="tablist" aria-label="Choose an attachment" data-testid={`${testid}-list`}>
          {list.map((a, i) => (
            <button key={a.key} type="button" role="tab" aria-selected={i === selected}
              onClick={() => setSelected(i)} data-testid={`${testid}-pick-${i}`}
              className={`inline-flex min-h-[2.75rem] max-w-full items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#034AA2] ${
                i === selected ? "bg-[#034AA2] text-white" : "bg-white border border-[#C2C9D1] text-[#374151] hover:bg-[#E9E9EB]"}`}>
              <Paperclip size={13} weight="bold" className="shrink-0" />
              <span className="min-w-0 truncate">{a.label || `Attachment ${i + 1}`}</span>
            </button>
          ))}
        </div>
      )}

      {list.length === 1 && current?.label && (
        <div className="mb-1.5 truncate text-xs font-semibold text-[#374151]" data-testid={`${testid}-name`}>{current.label}</div>
      )}

      {/* key= remounts the viewer when the reader switches attachments, which
          is what ReceiptViewer's own documentation asks the parent to do. */}
      <AttachmentBody key={current.key} attachment={current} testbase={testid} />
    </div>
  );
}

/**
 * Open/close state for a list: at most one panel open at a time.
 *
 * It also remembers the paperclip that opened the panel, so closing hands
 * focus straight back to it. Without that, closing with the keyboard drops
 * focus to the top of the page and the reader loses their place in a
 * forty-row list — which is a different way of losing the row.
 */
export function useAttachmentPanel() {
  const [openKey, setOpenKey] = useState(null);
  const buttons = useRef({});

  const focusButton = useCallback((k) => { buttons.current[k]?.focus?.(); }, []);
  const buttonRef = useCallback((k) => (el) => {
    if (el) buttons.current[k] = el; else delete buttons.current[k];
  }, []);

  return {
    openKey,
    isOpen: (k) => openKey === k,
    panelId: (k) => `attachments-${String(k).replace(/[^A-Za-z0-9_-]/g, "-")}`,
    buttonRef,
    toggle: (k) => setOpenKey((cur) => (cur === k ? null : k)),
    close: (k) => { setOpenKey(null); if (k != null) focusButton(k); },
  };
}

/**
 * Names and page counts for attachments that are RECEIPTS.
 *
 * Two screens store nothing about a receipt but its id — a gift keeps a bare
 * list of them, and an expense's attachment list carries a filename but not
 * how many pages a scanned PDF has. Both facts live on the receipts list the
 * app already reads elsewhere, so this fetches it ONCE, and only after
 * somebody has actually opened a panel: nobody pays for a lookup they never
 * asked for, and the answer is shared across every row on the page.
 *
 * A failure here is not fatal. The attachment still opens; it is just called
 * "Attachment 1" instead of by its filename. That is the right way round —
 * a naming lookup must never be what stops evidence being looked at.
 */
export function useReceiptIndex(enabled) {
  const { data } = useQuery({
    queryKey: ["receipt-index"],
    queryFn: async () => {
      const rows = (await api.get("/receipts")).data || [];
      const map = {};
      for (const r of rows) map[r.id] = r;
      return map;
    },
    enabled: !!enabled,
    staleTime: 60_000,
    retry: false,
  });
  return data || {};
}

/** One receipt id, turned into something the panel can show. */
export function receiptAttachment(id, index, fallbackLabel) {
  const r = index?.[id];
  return {
    key: id,
    label: r?.original_filename || fallbackLabel || "Attachment",
    fileUrl: receiptUrl(id),
    receiptId: id,
    pageCount: r?.page_count || 1,
  };
}
